import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/lib/api';
import { parseAsUtc } from '@/lib/dateUtils';
import type { ActiveTasksResponse, RecentIssue, ActiveTask } from '@/types';

/** Info about a recently completed task */
export interface CompletedTaskInfo {
  history_id: string;
  topic?: string;
  completed_at: string;
}

interface UseActiveTaskOptions {
  /** Polling interval in milliseconds (default: 5000) */
  pollInterval?: number;
  /** Whether to start polling immediately (default: true) */
  autoStart?: boolean;
  /** Callback when task completes, receives the completed task info */
  onComplete?: (completedTask?: CompletedTaskInfo) => void;
}

interface UseActiveTaskReturn {
  /** List of active tasks */
  activeTasks: ActiveTask[];
  /** Whether there are any active tasks */
  hasActiveTasks: boolean;
  /** Whether we're currently loading */
  isLoading: boolean;
  /** Any error that occurred */
  error: string | null;
  /** Manually refresh the active tasks */
  refresh: () => Promise<ActiveTasksResponse | null>;
  /** Start polling */
  startPolling: () => void;
  /** Stop polling */
  stopPolling: () => void;
  /** Elapsed time for first active task in seconds */
  elapsedSeconds: number;
  /** Recent issues/errors during generation (admin only) */
  recentIssues: RecentIssue[];
  /** Count of issues */
  issueCount: number;
  /** Info about the most recently completed task */
  lastCompletedTask: CompletedTaskInfo | null;
  /** Clear the last completed task info */
  clearCompletedTask: () => void;
}

/**
 * Hook to track active newsletter generation tasks with polling.
 * Automatically polls for updates when there are active tasks.
 */
export function useActiveTask(options: UseActiveTaskOptions = {}): UseActiveTaskReturn {
  const {
    pollInterval = 5000,
    autoStart = true,
    onComplete,
  } = options;

  const [activeTasks, setActiveTasks] = useState<ActiveTask[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [recentIssues, setRecentIssues] = useState<RecentIssue[]>([]);
  const [issueCount, setIssueCount] = useState(0);
  const [lastCompletedTask, setLastCompletedTask] = useState<CompletedTaskInfo | null>(null);

  const pollIntervalRef = useRef<number | null>(null);
  const elapsedIntervalRef = useRef<number | null>(null);
  const hadActiveTasksRef = useRef(false);
  const lastActiveTasksRef = useRef<ActiveTask[]>([]);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const clearCompletedTask = useCallback(() => {
    setLastCompletedTask(null);
  }, []);

  const fetchActiveTasks = useCallback(async () => {
    try {
      const response: ActiveTasksResponse = await api.getActiveNewsletters();
      setActiveTasks(response.tasks);
      setRecentIssues(response.recent_issues || []);
      setIssueCount(response.issue_count || 0);
      setError(null);

      // Check if tasks just completed (had active -> no active)
      if (hadActiveTasksRef.current && !response.has_active) {
        // Store info about the completed task before clearing
        const completedTask = lastActiveTasksRef.current[0];
        const completedInfo: CompletedTaskInfo | undefined = completedTask ? {
          history_id: completedTask.history_id,
          topic: completedTask.topic,
          completed_at: new Date().toISOString(),
        } : undefined;

        if (completedInfo) {
          setLastCompletedTask(completedInfo);
        }

        onCompleteRef.current?.(completedInfo);
      }

      hadActiveTasksRef.current = response.has_active;
      lastActiveTasksRef.current = response.tasks;

      return response;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch active tasks');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) return;
    
    pollIntervalRef.current = window.setInterval(() => {
      fetchActiveTasks();
    }, pollInterval);
  }, [fetchActiveTasks, pollInterval]);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // Update elapsed time every second when there are active tasks
  useEffect(() => {
    if (activeTasks.length > 0) {
      const updateElapsed = () => {
        const firstTask = activeTasks[0];
        if (firstTask?.started_at) {
          // Use parseAsUtc to properly handle UTC timestamps (with or without Z suffix)
          const startTime = parseAsUtc(firstTask.started_at).getTime();
          // Validate the parsed timestamp
          if (!isNaN(startTime) && startTime > 0) {
            const now = Date.now();
            // Guard against negative values (clock skew) and unreasonably large values
            const elapsed = Math.floor((now - startTime) / 1000);
            setElapsedSeconds(Math.max(0, elapsed));
          } else {
            // Invalid timestamp, default to 0
            setElapsedSeconds(0);
          }
        } else {
          // No started_at means generation hasn't actually started yet
          setElapsedSeconds(0);
        }
      };

      updateElapsed();
      elapsedIntervalRef.current = window.setInterval(updateElapsed, 1000);
    } else {
      setElapsedSeconds(0);
    }

    return () => {
      if (elapsedIntervalRef.current) {
        clearInterval(elapsedIntervalRef.current);
        elapsedIntervalRef.current = null;
      }
    };
  }, [activeTasks]);

  // Initial fetch and auto-start polling
  useEffect(() => {
    fetchActiveTasks();
    
    if (autoStart) {
      startPolling();
    }
    
    return () => {
      stopPolling();
    };
  }, [fetchActiveTasks, autoStart, startPolling, stopPolling]);

  return {
    activeTasks,
    hasActiveTasks: activeTasks.length > 0,
    isLoading,
    error,
    refresh: fetchActiveTasks,
    startPolling,
    stopPolling,
    elapsedSeconds,
    recentIssues,
    issueCount,
    lastCompletedTask,
    clearCompletedTask,
  };
}

